import { accountTypeGuide } from '../data';

const strategyList = [
  { key: 'scalping', label: 'Scalping', dominantCost: 'Commission', frequency: '400 trades/month', holding: 'No overnight' },
  { key: 'dayTrading', label: 'Day Trading', dominantCost: 'Spread + Commission', frequency: '100 trades/month', holding: 'Closed by 5 PM ET' },
  { key: 'swingTrading', label: 'Swing Trading', dominantCost: 'Swap / Rollover', frequency: '20 trades/month', holding: '2–20 days avg' },
  { key: 'positionTrading', label: 'Position Trading', dominantCost: 'Swap / Rollover', frequency: '6 trades/month', holding: '30+ days avg' },
] as const;

function parseMonthlyCost(raw: string): number {
  if (raw === 'N/A') return Infinity;
  return parseFloat(raw.replace(/[~$,]/g, '')) || Infinity;
}

function fmtCost(raw: string): string {
  if (raw === 'N/A') return 'N/A';
  return raw;
}

export default function AccountTypeMatchmaker() {
  return (
    <section className="relative w-full bg-background-100 overflow-hidden">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-14 md:py-20">

        {/* Header */}
        <div className="mb-10 md:mb-14">
          <h2 className="text-[28px] md:text-[46px] font-bold text-foreground-950 tracking-[-0.02em] leading-[1.05] max-w-[620px]">
            Your strategy picks the account — not the other way around.
          </h2>
          <p className="mt-4 md:mt-5 text-[15px] text-foreground-600 leading-relaxed max-w-[620px]">
            Same trader, same pairs, same frequency. The only variable is the account type — and it can multiply your costs by up to 5×.
          </p>
        </div>

        {/* Strategy blocks: 2×2 grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
          {strategyList.map((strategy) => {
            const ranked = accountTypeGuide
              .map((a) => ({
                ...a,
                costRaw: a.monthlyEstimate[strategy.key],
                costNum: parseMonthlyCost(a.monthlyEstimate[strategy.key]),
              }))
              .sort((a, b) => a.costNum - b.costNum);

            const valid = ranked.filter((a) => isFinite(a.costNum));
            const maxCost = valid.length > 0 ? Math.max(...valid.map((a) => a.costNum)) : 1;
            const best = valid[0];
            const worstCostNum = valid.length > 0 ? valid[valid.length - 1].costNum : Infinity;
            const tiedForWorst = valid.filter((a) => a.costNum === worstCostNum && a.costNum !== best?.costNum);
            const savings = isFinite(worstCostNum) && isFinite(best?.costNum) ? worstCostNum - best.costNum : 0;

            return (
              <div key={strategy.key}>
                {/* Strategy identity */}
                <div className="mb-5 md:mb-6">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <h3 className="text-[21px] md:text-[26px] font-bold text-foreground-950 tracking-[-0.01em]">
                      {strategy.label}
                    </h3>
                    <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-wider text-foreground-500">
                      {strategy.frequency}
                    </span>
                  </div>
                  <p className="mt-1.5 text-[12px] md:text-[13px] text-foreground-500">
                    Dominant cost: <span className="font-semibold text-foreground-700">{strategy.dominantCost}</span>
                    <span className="mx-2 text-foreground-300">·</span>
                    {strategy.holding}
                  </p>
                </div>

                {/* Cost league table */}
                <div className="space-y-0">
                  {ranked.map((account, idx) => {
                    const isBest = idx === 0 && isFinite(account.costNum);
                    const isWorst = isFinite(account.costNum) && account.costNum === worstCostNum && !isBest;
                    const isNA = !isFinite(account.costNum);
                    const barPct = isNA ? 0 : Math.max((account.costNum / maxCost) * 100, 3);
                    const ratioVsBest =
                      isFinite(account.costNum) && isFinite(best?.costNum) && best.costNum > 0
                        ? account.costNum / best.costNum
                        : 1;
                    const tiedCount = tiedForWorst.length;

                    return (
                      /* border-b lives on wrapper so mobile specs row sits above the divider */
                      <div key={account.accountType} className="group relative border-b border-background-200/60">

                        {/* Main row */}
                        <div className={`flex items-center gap-3 md:gap-4 py-3 ${isNA ? 'opacity-50' : ''}`}>

                          {/* Account name — narrower on mobile */}
                          <div className="w-[120px] sm:w-[155px] flex-shrink-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`text-[12px] md:text-[13px] font-semibold leading-snug ${isBest ? 'text-foreground-950' : isNA ? 'text-foreground-400' : 'text-foreground-700'}`}>
                                {account.accountType}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {isBest && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                                  Best match
                                </span>
                              )}
                              {isWorst && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-foreground-100 text-foreground-500 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                                  {tiedCount > 1 ? `Avoid (${tiedCount})` : 'Avoid'}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Bar area */}
                          <div className="flex-1 min-w-0">
                            {isNA ? (
                              <div className="flex items-center h-7">
                                <span className="w-5 h-px bg-foreground-200 mr-2 flex-shrink-0"></span>
                                <span className="text-[11px] text-foreground-400 italic leading-snug">
                                  N/A
                                  {/* Show full text only on sm+ */}
                                  <span className="hidden sm:inline">
                                    {account.bestFor.length > 0 && ` — for ${account.bestFor.join(', ')}`}
                                  </span>
                                </span>
                              </div>
                            ) : (
                              <div className="relative h-7 flex items-center">
                                <div
                                  className={`h-5 rounded-r-sm transition-all duration-500 cursor-default relative ${
                                    isBest ? 'bg-accent-500' : isWorst ? 'bg-foreground-200' : 'bg-foreground-100'
                                  }`}
                                  style={{ width: `${barPct}%`, minWidth: isBest ? '6px' : '3px' }}
                                >
                                  {/* Tooltip — desktop only (no hover on mobile) */}
                                  <div className="hidden md:block absolute left-1/2 -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                                    <div className="bg-foreground-900 text-background-50 text-[11px] px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                                      {ratioVsBest >= 2
                                        ? `${ratioVsBest.toFixed(1)}× the best option`
                                        : ratioVsBest >= 1.05
                                        ? `${((ratioVsBest - 1) * 100).toFixed(0)}% more than best`
                                        : 'Best value'}
                                    </div>
                                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-foreground-900"></div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Cost */}
                          <div className="w-[62px] sm:w-[72px] flex-shrink-0 text-right">
                            <span className={`text-[13px] md:text-[14px] font-bold tabular-nums ${isBest ? 'text-foreground-950' : isNA ? 'text-foreground-400' : isWorst ? 'text-foreground-600' : 'text-foreground-700'}`}>
                              {fmtCost(account.costRaw)}
                            </span>
                            {!isNA && <span className="text-[10px] md:text-[11px] text-foreground-400 ml-0.5">/mo</span>}
                          </div>
                        </div>

                        {/* Mobile: specs always visible (condensed) */}
                        {!isNA && (
                          <div className="md:hidden pb-2.5 flex flex-wrap gap-x-4 gap-y-0.5">
                            <span className="text-[10px] text-foreground-400">
                              Spd: <span className="font-semibold text-foreground-600">{account.spread}</span>
                            </span>
                            <span className="text-[10px] text-foreground-400">
                              Com: <span className="font-semibold text-foreground-600">{account.commission}</span>
                            </span>
                            <span className="text-[10px] text-foreground-400">
                              Swap: <span className="font-semibold text-foreground-600">{account.swapMarkup}</span>
                            </span>
                          </div>
                        )}

                        {/* Desktop: hover reveal specs */}
                        <div className="hidden md:block overflow-hidden max-h-0 group-hover:max-h-32 transition-all duration-300">
                          <div className="px-4 pb-3 pt-1 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-foreground-500">
                            <span>Spread: <span className="font-semibold text-foreground-700">{account.spread}</span></span>
                            <span>Commission: <span className="font-semibold text-foreground-700">{account.commission}</span></span>
                            <span>Swap: <span className="font-semibold text-foreground-700">{account.swapMarkup}</span></span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Savings callout */}
                {savings > 0 && (
                  <div className="mt-4 md:mt-5 pt-4 border-t-2 border-accent-200/70">
                    <div className="flex items-baseline gap-x-2 gap-y-1 flex-wrap">
                      <span className="text-[13px] font-bold text-foreground-900">{best.accountType}</span>
                      <span className="text-[12px] text-foreground-500">saves you</span>
                      <span className="text-[20px] md:text-[22px] font-bold text-foreground-950 tabular-nums tracking-[-0.01em]">${savings.toLocaleString()}</span>
                      <span className="text-[12px] text-foreground-400">/mo</span>
                    </div>
                    <div className="mt-1.5 flex items-baseline gap-x-2 gap-y-1 flex-wrap">
                      <span className="text-[12px] text-foreground-500">
                        over {tiedForWorst.map((a) => a.accountType).join(' & ')}
                        {tiedForWorst.length > 1 ? ' (tied)' : ''}
                      </span>
                      <span className="text-[12px] text-foreground-300">—</span>
                      <span className="text-[13px] font-semibold text-foreground-700 tabular-nums">
                        ${(savings * 12).toLocaleString()}/year
                      </span>
                    </div>
                  </div>
                )}

                {/* Verdict */}
                {best && (
                  <div className="mt-3">
                    <p className="text-[12px] text-foreground-500 leading-relaxed italic">
                      {best.keyVerdict}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom: simplified decision rules */}
        <div className="mt-14 md:mt-24 pt-8 md:pt-10 border-t border-background-200">
          <p className="text-[11px] md:text-[13px] font-bold uppercase tracking-[0.12em] text-foreground-500 mb-5 md:mb-6">
            The simple rule
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-8">
            <div>
              <div className="flex items-center gap-2 mb-2.5 md:mb-3">
                <span className="w-1 h-4 rounded-full bg-accent-500 flex-shrink-0"></span>
                <span className="text-[13px] md:text-[14px] font-bold text-foreground-900">Scalpers</span>
              </div>
              <p className="text-[12px] md:text-[13px] text-foreground-600 leading-relaxed">
                Pro/VIP if you qualify ($25K+). At 400 trades/month, halved commissions and tighter spreads save $160/month over ECN — that&apos;s $1,920/year. If not, ECN is still 3× cheaper than Standard.
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2.5 md:mb-3">
                <span className="w-1 h-4 rounded-full bg-primary-500 flex-shrink-0"></span>
                <span className="text-[13px] md:text-[14px] font-bold text-foreground-900">Day traders</span>
              </div>
              <p className="text-[12px] md:text-[13px] text-foreground-600 leading-relaxed">
                ECN beats Standard at any volume — tighter spreads more than offset the commission. At 100+ trades/month, Pro/VIP pulls ahead further. Standard only makes sense if you trade micro lots or prefer simplicity over cost.
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2.5 md:mb-3">
                <span className="w-1 h-4 rounded-full bg-secondary-500 flex-shrink-0"></span>
                <span className="text-[13px] md:text-[14px] font-bold text-foreground-900">Swing traders</span>
              </div>
              <p className="text-[12px] md:text-[13px] text-foreground-600 leading-relaxed">
                Swap-Free if eligible — it cuts your dominant cost by 55%. If not, ECN and Standard cost the same at this frequency. A 0.2 pip/day swap difference on a 2-lot position held 7 days costs $28.
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2.5 md:mb-3">
                <span className="w-1 h-4 rounded-full bg-foreground-500 flex-shrink-0"></span>
                <span className="text-[13px] md:text-[14px] font-bold text-foreground-900">Position traders</span>
              </div>
              <p className="text-[12px] md:text-[13px] text-foreground-600 leading-relaxed">
                Swap-Free if eligible. At 30-day holds, swap is 70% of total costs — entry costs are a footnote by comparison. If swap-free is not available, compare swap rates — a 0.1 pip/day difference on 5 lots over 30 days is $135.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}